import { putPackageResetLang } from './../../api/package';
import { useHistory } from 'react-router-dom'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query'
import { useIntl } from 'react-intl'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'

import { PackageInfo } from './../../types/index'
import {
  getPackageWithStickerAPI,
  postPackageAPI,
  putPackageAPI,
  putPackageStatusEditingAPI,
  submitPackageAPI,
} from '../../api/package'
import { processAnExpiredSession } from '../../utils/errorHandling'
import { notiFormForError } from '../../components/notificationForm'
import rootHistory from '../../rootHistory'
import { savePkgNoti } from '../../components/NotiCustom'

export const PACKAGE_INFO_KEY = 'package'
export const CREATE_PACKAGE_KEY = 'package-create'

export function usePackageInfo(
  packageId: string | undefined,
  sessionToken: string | undefined,
): UseQueryResult<PackageInfo, unknown> {
  return useQuery(
    ['package', packageId],
    () =>
      getPackageWithStickerAPI({
        packageId,
        sessionToken,
      }),
    {
      enabled: !!packageId && sessionToken !== undefined,
      onError: (e: AxiosError) => {
        console.dir(e)
        switch (e.response?.status) {
          case 403:
            processAnExpiredSession()
            break
          default:
            Sentry.captureException(
              new Error(`get package id:${packageId} message ::` + e.message),
            )
        }
      },
    },
  )
}

export const useCreatePackage = () => {
  const intl = useIntl()
  return useMutation(postPackageAPI, {
    mutationKey: CREATE_PACKAGE_KEY,
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        case 800:
          notiFormForError(
            intl.locale,
            intl.formatMessage({ id: 'PackageInfo.error.nameOverlap' }),
          )
          break
        default:
          Sentry.captureException(
            new Error(`create package === message ::` + e.message),
          )
      }
    },
  })
}

export const useEditPackage = (packageId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()

  return useMutation(putPackageAPI, {
    mutationKey: packageId,
    onSuccess: (data) => {
      queryClient.invalidateQueries([PACKAGE_INFO_KEY, data.objectId], {
        refetchActive: true,
        refetchInactive: false,
      })
      savePkgNoti(
        'success',
        intl.formatMessage({ id: 'PkgConfirm.modal.success.title' }),
        '',
      )
    },
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        case 800:
          notiFormForError(
            intl.locale,
            intl.formatMessage({ id: 'PackageInfo.error.nameOverlap' }),
          )
          break
        default:
          Sentry.captureException(
            new Error(`edit package id:${packageId} message ::` + e.message),
          )
      }
    },
  })
}

export const useSubmitPackage = (packageId: string) =>
  useMutation(submitPackageAPI, {
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        default:
          Sentry.captureException(
            new Error(`submit package id:${packageId} message ::` + e.message),
          )
      }
    },
    mutationKey: packageId,
  })

export const usePutPackageStatus = (packageId: string) => {
  const intl = useIntl()

  return useMutation(putPackageStatusEditingAPI, {
    mutationKey: packageId,
    onSuccess: () => {
      rootHistory.push(`/upload/package/${packageId}/sticker-upload`)
    },
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        default:
          notiFormForError(
            intl.locale,
            intl.formatMessage({ id: 'Upload.packages.serverError' }),
          )
          Sentry.captureException(
            new Error(
              `make package edit from reject id:${packageId} message ::` +
                e.message,
            ),
          )
      }
    },
  })
}

export const useResetPackageLang = (packageId: string)=>{
  return useMutation(putPackageResetLang, {
    mutationKey: packageId,
    onSuccess: () => {
      rootHistory.push(`/upload/package/${packageId}/sticker-upload`)
    },
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        default:
          Sentry.captureException(
            new Error(
              `make package edit from reject id:${packageId} message ::` +
                e.message,
            ),
          )
      }
    },
  })
}